// @file Whiteboard AI helper functions.
import environment from '@@/bits/environment'
import window from '@@/bits/global'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { requestIdleCallback } from '@@/bits/request_idle_callback'
import {
  getSvgStringFromShapeIds,
  SVG_TO_PNG_WORKER_BASE_URL,
  SVG_TO_PNG_WORKER_BASE_URL_SUFFIX,
} from '@@/bits/whiteboard_preview_utils'
import type { TLShapeId } from 'tldraw'

interface LogPayload {
  userMessage: string
  shapeIds: TLShapeId[]
}

async function getScreenshotOfShapeIds({
  userMessage,
  shapeIds,
}: {
  userMessage: string
  shapeIds: TLShapeId[]
}): Promise<string> {
  if (shapeIds.length === 0) {
    return ''
  }
  try {
    const svg = await getSvgStringFromShapeIds(shapeIds)
    const userMessageCacheKey = encodeURIComponent(
      `whiteboard-ai-${environment}-${new Date().getTime()}-${userMessage.slice(0, 20)}`,
    )
    const pngUrl = `${SVG_TO_PNG_WORKER_BASE_URL}${userMessageCacheKey}${SVG_TO_PNG_WORKER_BASE_URL_SUFFIX}?scale=3`
    const response = await fetch(pngUrl, {
      method: 'POST',
      body: svg,
    })
    if (response.ok) {
      return pngUrl
    }
    return ''
  } catch {
    return ''
  }
}

async function getScreenshotAndLog({ userMessage, shapeIds }: LogPayload): Promise<void> {
  const screenshotUrl = await getScreenshotOfShapeIds({
    userMessage,
    shapeIds,
  })

  // Results are logged to this Padlet board:
  // https://padlet.com/starkindustries/sandbox-ai-prompts-7woqis4mxhd01fps
  await fetch('https://whiteboard-ai-logger.padlet.workers.dev', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: asciiSafeStringify({
      userMessage,
      sandboxUrl: window.location.href,
      screenshotUrl,
    }),
  }).catch(() => {
    // No-op
  })
}

function logCreatedShapes(payload: LogPayload): void {
  requestIdleCallback(() => {
    void getScreenshotAndLog(payload)
  })
}

export { getScreenshotOfShapeIds, logCreatedShapes }
